import { Injectable } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, AbstractControl } from '@angular/forms';
import _has from 'lodash-es/has';
import { environment } from '@environment';
import {
  AutocompleteControl,
  BaseControl,
  CheckboxControl,
  DateControl,
  DateIntervalControl,
  DatetimeControl,
  DatetimeIntervalControl,
  FieldValidators,
  FormField,
  FormFieldSerializedValue,
  NumberControl,
  SelectControl,
  TextControl,
  TextareaControl,
  FieldType,
  RadioButtonControl,
  FormFieldSerializedValues,
  FileControl,
  WysiwygControl,
  ScheduleControl,
  OverbookingGridControl,
} from '@shared/shared/form-fields';
import { SearchLocalControl } from '@shared/shared/form-fields/types/controls/search-local';

const FORM_FIELD_CONTROLS = {
  [FieldType.autocomplete]: AutocompleteControl,
  [FieldType.checkbox]: CheckboxControl,
  [FieldType.date]: DateControl,
  [FieldType.dateInterval]: DateIntervalControl,
  [FieldType.datetime]: DatetimeControl,
  [FieldType.datetimeInterval]: DatetimeIntervalControl,
  [FieldType.file]: FileControl,
  [FieldType.number]: NumberControl,
  [FieldType.overbookingGrid]: OverbookingGridControl,
  [FieldType.schedule]: ScheduleControl,
  [FieldType.select]: SelectControl,
  [FieldType.text]: TextControl,
  [FieldType.textarea]: TextareaControl,
  [FieldType.radioButton]: RadioButtonControl,
  [FieldType.wysiwyg]: WysiwygControl,
  [FieldType.checkboxSlider]: CheckboxControl,
  [FieldType.searchLocal]: SearchLocalControl,
};

@Injectable({
  providedIn: 'root',
})
export class FormFieldsService {
  getFormControl(formField: FormField): UntypedFormControl {
    return this.makeField(formField).createFormControl();
  }

  updateFormControlValue(formControl: AbstractControl, formField: FormField): void {
    this.makeField(formField).updateFormControlValue(formControl);
  }

  getValidatorConstraint(formField: FormField, validatorName: keyof FieldValidators): any {
    return this.makeField(formField).getValidatorConstraint(validatorName);
  }

  serializeValues(formGroup: UntypedFormGroup, formFields: FormField[]): FormFieldSerializedValues {
    return formFields.reduce(
      (result, field: FormField) => ({
        ...result,
        [field.key]: this.serializeValue(field, formGroup.get(field.key).value),
      }),
      {},
    );
  }

  private makeField(formField: FormField): BaseControl {
    if (_has(FORM_FIELD_CONTROLS, formField.type)) {
      const ControlClass = FORM_FIELD_CONTROLS[formField.type];

      return new ControlClass(formField, {
        dateFormat: formField.dateFormat || environment.serverDateFormat,
        dateTimeFormat: formField.dateTimeFormat || environment.serverDateTimeFormat,
      });
    }

    throw new TypeError(`Unknown field type ${formField.type}`);
  }

  private serializeValue(resultField: FormField, formControlValue: any): FormFieldSerializedValue {
    return this.makeField(resultField).serializeValue(formControlValue);
  }
}
